import type { WhiteLabel } from '@skytech/types'
import { isObject } from 'lodash'

import type { useTranslation } from '@/app/translations/translations-hooks'
import type { Asset } from '@/modules/admin/resources'
import { leasingAssetActions } from '@/modules/admin/resources/assets/constants'

export function monthDiff(startDate: Date, endDate: Date) {
  let months = 0
  months = (endDate.getFullYear() - startDate.getFullYear()) * 12
  months -= startDate.getMonth()
  months += endDate.getMonth()
  return months
}

export function getFilterByKey({
  queryAssetAge,
  queryAssetType,
  search,
}: {
  queryAssetAge: string | null
  queryAssetType: string | null
  search: Location['search']
}) {
  const typeIdx = search.indexOf('assetType')
  const ageIdx = search.indexOf('assetAge')
  let filterByKey = ''

  if ((queryAssetType && !queryAssetAge) || (queryAssetType && typeIdx < ageIdx)) {
    filterByKey = 'assetType'
    return filterByKey
  }
  if ((queryAssetAge && !queryAssetType) || (queryAssetAge && ageIdx < typeIdx)) {
    filterByKey = 'assetAge'
    return filterByKey
  }

  return ''
}

export function getSelectOptions<T extends string>(
  options:
    | { id: number | string; label: T | { id: string; values: Record<string, string> } }[]
    | undefined,
  translate: ReturnType<typeof useTranslation>
) {
  return (options || []).map(option => ({
    label: isObject(option.label)
      ? translate(option.label.id, option.label.values)
      : translate(option.label),
    value: option.id,
  }))
}

export function getIsWithinTimePeriod(daysAhead: number, targetDate: Date) {
  const now: Date = new Date()
  const futureDate: Date = new Date(now.getTime() + daysAhead * 24 * 60 * 60 * 1000)
  return targetDate <= futureDate
}

const MonthExtensionPattern = /^(\d+)_MONTH_EXTENSION$/

export const getMonthNumber = (option: string): number | null => {
  const match = option.match(MonthExtensionPattern)
  return match ? parseInt(match[1], 10) : null
}

const sortFn = (a: string | number, b: string | number) => {
  if (typeof a === 'number' && typeof b === 'string') return 1
  if (typeof a === 'string' && typeof b === 'number') return -1
  if (typeof a === 'number' && typeof b === 'number') return a - b
  return 0
}

export const filterAndSortOptions = (activeEndingOptions: string[]): (string | number)[] => {
  if (!activeEndingOptions || activeEndingOptions.length === 0) return []

  const filtered = activeEndingOptions.filter(option => option !== leasingAssetActions.BUYOUT) || []
  const optionsByNumber = filtered
    .map(option => {
      const number = getMonthNumber(option)
      return number === null ? option : number
    })
    .sort(sortFn)
  return optionsByNumber
}

export const isLeasedAssetsList = (arr: Asset[]): boolean =>
  Array.isArray(arr) && arr.every(obj => obj.isLeased === true)

export const leaseAssetTopBgStyleByWhiteLabel: Partial<Record<WhiteLabel, string>> = {
  skytech: 'bg-skytech-orange/10',
  telia: 'bg-telia/10',
  threeStepIT: 'bg-threeStepIT-seaMistPrimary/30',
  mobit: 'bg-mobit/10',
  evry: 'bg-tietoevry/10',
  tietoevry: 'bg-tietoevry/10',
  bechtle: 'bg-bechtle/10',
  csi: 'bg-csi-maroon/10',
  arp: 'bg-arp/10',
  elkjop: 'bg-elkjop/10',
  komplett: 'bg-komplett-lightYellow/10',
}
